import { findOrCreateDeviceId } from "@theconversation/shared-components/tcEventsCollector"
import { Bugsnag } from "common/lib/bugsnag"
import { EXPERIMENTS } from "./experimentConfig"
import { ExperimentAssignment, ExperimentContext, ExperimentVariant } from "./types"

const PRODUCTION_DOMAIN = "theconversation.com"

/**
 * Generates a deterministic hash bucket (0-99) from device ID and experiment ID.
 * The same device ID + experiment ID combination will always produce the same bucket.
 *
 * @param deviceId - The user's device identifier
 * @param experimentId - The experiment ID (ULID)
 * @returns A number between 0 and 99 for bucket assignment
 */
export function hashToBucket(deviceId: string, experimentId: string): number {
  const input = `${deviceId}:${experimentId}`
  let hash = 0

  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }

  // Ensure a positive number and constrain to 0-99
  return Math.abs(hash) % 100
}

/**
 * Assigns a variant based on the bucket number and variant weights.
 * Uses cumulative weight distribution for assignment.
 *
 * @param bucket - The bucket number (0-99)
 * @param variants - Array of variants with their weights
 * @returns The ID of the assigned variant
 */
export function assignVariant(bucket: number, variants: ExperimentVariant[]): string {
  let cumulative = 0

  for (const variant of variants) {
    cumulative += variant.weight
    if (bucket < cumulative) {
      return variant.id
    }
  }

  // Fallback to the first variant if weights don't sum to 100 or other edge cases
  const message = `Experiment variant assignment fallback: bucket ${bucket} >= cumulative weight ${cumulative}. Variants: ${JSON.stringify(variants)}`

  if (window.location.hostname !== PRODUCTION_DOMAIN) {
    console.warn(message, {
      bucket,
      variants,
      cumulative,
    })
  } else {
    Bugsnag.notify(new Error(message), (event) => {
      event.severity = "info"
      event.addMetadata("experiment", {
        bucket,
        variants,
        cumulative,
      })
    })
  }

  return variants[0].id
}

export function getExperimentAssignment(
  experimentId: string,
  context: ExperimentContext,
): ExperimentAssignment | null {
  const config = EXPERIMENTS[experimentId]
  const { isSignedIn, forcedState } = context

  // Check if the experiment exists
  if (!config) {
    return null
  }

  // Check if the experiment is enabled.
  const isEnabled =
    forcedState === "enabled" ? true : forcedState === "disabled" ? false : config.enabled

  if (!isEnabled) {
    return null
  }

  // Check if user should be included
  if (config.filters.excludeLoggedInUsers && isSignedIn) {
    return null
  }

  const deviceId = findOrCreateDeviceId()
  const bucket = hashToBucket(deviceId, experimentId)
  const variantId = assignVariant(bucket, config.variants)

  return { variantId }
}
